/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useState } from 'react'
import { ListView } from 'antd-mobile'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { isMobile } from 'react-device-detect'
import NewIco from '@assets/images/icon/etc_icon/new-ico.png'
import { FriendsListWrap } from '../index.style'
import dayjs from 'dayjs'

const FriendsList: React.FC = () => {

  const { componentStore } = useRootStore()
  const { friendInvitationInvitor } = componentStore

  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [users, setUsers] = useState(null)
  const [dataSource, setDataSource] = useState(new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  }))

  useEffect(() => {
    if (_.isEmpty(friendInvitationInvitor)) {
      return
    }

    fetch(true)
  }, [friendInvitationInvitor])

  const fetch = async (reset = false) => {
    try {
      setIsLoading(true)

      const { startOfMonth, betMoneyType } = friendInvitationInvitor
      const params = {
        targetAt: startOfMonth,
        betMoneyType,
        offset: reset ? 0 : _.size(users),
      }
      const result = await componentStore.api.component.getFriendInvitationInvitedUsers(params)
      let newUsers = result.users
      if (!reset) {
        newUsers = _.concat(users, result.users)
      }

      setUsers(newUsers)
      setDataSource(dataSource.cloneWithRows(newUsers))
      setHasMore(result.hasMore)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const calculateUserBettingMoneySummaryPercent = (userSummary: number) => {
    if (_.isNil(userSummary) || _.toNumber(userSummary) === 0) {
      return 0
    }

    const totalSummary = _.toNumber(
      _.find(friendInvitationInvitor?.invitedUsersBettingMoneySummary, targetObject => {
        return _.toNumber(targetObject?.betMoneyType) === _.toNumber(friendInvitationInvitor.betMoneyType)
      })?.membershipClubBettingMoney || 0
    )
    let percent = _.toNumber(userSummary) / _.toNumber(totalSummary) * 100
    if (percent < 0) {
      percent = 0
    } else if (percent < 1) {
      percent = Math.floor(percent * 10000) / 10000
    } else {
      percent = Math.floor(percent * 10) / 10
    }
    return percent
  }

  const renderRow = row => {
    const startOfMonthTime = dayjs(friendInvitationInvitor.startOfMonth).startOf('month').toDate().getTime()
    const percent = calculateUserBettingMoneySummaryPercent(row.bettingMoneySummary)
    const isNewUser = dayjs(row.invitedJoinAt).startOf('month').toDate().getTime() === startOfMonthTime
    return (
      // 베팅 금액 0%: no-bet
      <span className={`friends-list--item ${percent === 0 && 'no-bet'}`}>
        {isNewUser && <img src={NewIco} alt="신규" className="new-badge"/>}
        <span className="nick">
          {row.nick}
        </span>
        <div className="bar-chart">
          <span className="bar" style={{ width: `${percent}%` }}/>
          <span className="value">{percent}%</span>
        </div>
      </span>
    )
  }

  const renderFooter = () => {
    let footer = <Fragment/>
    if (isLoading) {
      footer = <Fragment/>
    } else if (!hasMore) {
      footer =
        <Fragment>
          {!_.isNil(users) && _.isEmpty(users) && (<p className="no-friends-txt">승부예측한 친구가 없습니다.</p>)}
          <div className="notice">
            <h3 className="tit">유의사항</h3>
            <p className="txt">무분별한 계정 생성을 통한 어뷰징은 운영정책에 따라 불이익을 당하실 수 있습니다.</p>

            <h4 className="sub-tit">계정</h4>
            <ul className="notice-list">
              <li className="notice-list--item">친구 초대 현황은 본인 명의 계정에 모두 공유됩니다.</li>
              <li className="notice-list--item">초대를 통해 가입한 경우 다른 추천인으로 변경이 불가합니다.</li>
              <li className="notice-list--item">친구 초대의 수는 무제한 가능합니다.</li>
              <li className="notice-list--item">이미 가입된 회원은 친구 초대가 불가능합니다.</li>
            </ul>

            <h4 className="sub-tit">베팅</h4>
            <ul className="notice-list">
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              <li className="notice-list--item">친구의 승부예측은 승부사온라인에서 지정한 클럽에서만 유효합니다.<br/>(클럽명: '국가대표365', '강원랜드', '승부사월드')</li>
              <li className="notice-list--item">친구의 승부예측은 G머니와 다이아를 이용한 베팅만 유효합니다.</li>
              <li className="notice-list--item">지급된 보상은 메시지함에서 수령할 수 있습니다.</li>
            </ul>

            <h4 className="sub-tit">기타</h4>
            <ul className="notice-list">
              <li className="notice-list--item">친구의 승부예측 금액은 마감 시 갱신됩니다.</li>
              <li className="notice-list--item">보상은 매월 1일 00시에 초기화됩니다.</li>
              <li className="notice-list--item">친구 초대 이벤트는 당사 사정에 의해 사전 공지 없이 내용이 변경되거나 기간이 조정될 수 있음을 안내 드립니다.</li>
            </ul>
          </div>
        </Fragment>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const endReachedHandler = async () => {
    if (isLoading) {
      return
    }

    if (hasMore) {
      await fetch()
    }
  }

  return (
    <FriendsListWrap>
      <p className="tit">승부예측한 친구</p>
      <div className="number">
        {friendInvitationInvitor?.invitedBettingUserCount}<span className="total">/{friendInvitationInvitor?.invitedUserCount}명</span>
      </div>
      <ListView
        dataSource={dataSource}
        renderRow={renderRow}
        scrollRenderAheadDistance={500}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        onEndReachedThreshold={10}
        useBodyScroll={isMobile}
        pageSize={20}
        className={isMobile ? 'friends-list' : 'friends-list pc'}/>
    </FriendsListWrap>
  )
}

export default observer(FriendsList)
